// ─────────────────────────────────────────────────────────────────────────────
// FileRowComponent
// ─────────────────────────────────────────────────────────────────────────────
// Expandable table row for a single knowledge-base document.
// Responsibilities:
//   - Display file name, type, status badge, upload date
//   - Toggle expanded panel with AI overview, internal notes, tags
//   - Dispatch delete, edit, and metadata update actions
// ─────────────────────────────────────────────────────────────────────────────

import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { DATE_TIME_FORMAT } from 'src/app/config/global.config';
import { SharedModule } from 'src/app/shared/shared.module';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { CheckboxComponent } from 'src/app/ui/checkbox/checkbox.component';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';

import { UiCheckboxChange } from 'src/app/shared/models/selection.models';

import { IKbDocument, IKbRequestMetadata, IKbUpdateRequest } from '../../../models/knowledge-base.models';
import * as KbActions from '../../../store/knowledge-base.actions';
import * as KbSelectors from '../../../store/knowledge-base.selectors';
import { KbConfirmationDialogComponent } from '../../kb-confirmation-dialog/kb-confirmation-dialog.component';
import { UploadDocumentDialogComponent } from '../upload-document-dialog/upload-document-dialog.component';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

@Component({
  selector: 'app-file-row',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonComponent, CheckboxComponent, SvgIconComponent, SharedModule],
  templateUrl: './file-row.component.html',
  styleUrls: ['./file-row.component.scss'],
})
export class FileRowComponent implements OnInit, OnDestroy {
  @Input() document!: IKbDocument;
  @Input() selected = false;
  /**
   * Emits the whole ui-checkbox payload, not just the new checked state: the
   * parent owns the selection and needs the keyboard modifiers to resolve a
   * shift-range, and they are only observable at the point of activation.
   */
  @Output() selectionChange = new EventEmitter<UiCheckboxChange>();

  // ── Public state ───────────────────────────────────────────────────────

  isExpanded = false;
  editableNotes = '';

  // ── Global config ───────────────────────────────────────────────────────

  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  // ── Permissions ─────────────────────────────────────────────────────────
  canEditKnowledgeBase$ = this.store.select(selectCanDo('knowledge_base', 'edit'));

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.editableNotes = this.document.userComment ?? '';

    this.store
      .select(KbSelectors.selectIsDocumentExpanded(this.document.id))
      .pipe(takeUntil(this.destroy$))
      .subscribe((expanded) => (this.isExpanded = expanded));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  toggleExpanded(): void {
    this.store.dispatch(KbActions.ToggleDocumentExpanded({ documentId: this.document.id }));
  }

  onDelete(): void {
    this.dialog.open(KbConfirmationDialogComponent, {
      data: { action: 'delete' as const, documentIds: [this.document.id] },
      disableClose: true,
    });
  }

  onEdit(): void {
    this.dialog.open(UploadDocumentDialogComponent, {
      width: '900px',
      disableClose: false,
      data: { document: this.document },
    });
  }

  onRemoveTag(tag: string): void {
    const updatedTags = this.document.tags.filter((t) => t !== tag);
    this.dispatchUpdate({ tags: updatedTags });
  }

  onNotesBlur(): void {
    if (this.editableNotes !== (this.document.userComment ?? '')) {
      this.dispatchUpdate({ user_comment: this.editableNotes || undefined });
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  get statusBadgeClass(): string {
    return `file-row__status-badge--${this.document.status}`;
  }

  /** Build the full metadata from the current document, apply overrides, and dispatch UpdateDocument. */
  private dispatchUpdate(overrides: Partial<IKbRequestMetadata>): void {
    const doc = this.document;
    const metadata: IKbRequestMetadata = {
      document_type: doc.type,
      document_types: doc.types?.length ? doc.types : [doc.type],
      tags: doc.tags,
      user_comment: doc.userComment ?? undefined,
      ...(doc.customerScope
        ? {
            customer_scope: {
              scope: doc.customerScope.scope,
              company_ids: doc.customerScope.companyIds,
              group_ids: doc.customerScope.groupIds,
            },
          }
        : {}),
      ...(doc.productScope
        ? {
            product_scope: {
              scope: doc.productScope.scope,
              brand_ids: doc.productScope.brandIds,
              category_ids: doc.productScope.categoryIds,
              label_ids: doc.productScope.labelIds,
              product_ids: doc.productScope.productIds,
            },
          }
        : {}),
      ...overrides,
    };

    const request: IKbUpdateRequest = { documentId: doc.id, metadata };
    this.store.dispatch(KbActions.UpdateDocument({ request }));
  }
}
